import { Box, Divider, IconButton, Stack, styled, SvgIcon, Typography, useTheme } from '@mui/material';

import { Box as BoxIcon, Calendar, ChevronsRight } from 'react-feather';

// Components
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';

// Utilities
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import TranslateText from 'src/components/common/TranslateText';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

const TypographyWhite = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  overflow: 'hidden',
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
}));

const IconButtonWhite = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: 16,
  border: `1px solid ${theme.palette.common.white}`,
}));

const StyledIconWhite = styled(SvgIcon)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: 16,
}));

const TradeDataInformation = ({ tradeDataDetail }: { tradeDataDetail: IBillOfLadingDetail }) => {
  const theme = useTheme();
  const { checkRestriction } = useProvisionRestriction();
  // const { handlePrint } = usePrinter();
  const checkRestrictionBol = checkRestriction({
    countryCode: tradeDataDetail?.buyer?.country_code || '',
    typeData: LIST_RESTRICTION_DATA.BOL,
  });
  return (
    <StyledBox position="relative">
      <Stack direction="row" alignItems="center" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
        <Box display="flex" flexDirection="column" flex={2}>
          <TypographyWhite align="left" variant="h5" gutterBottom>
            {renderTextOrDefault(tradeDataDetail?.transaction?.bol)}
            {isEmpty(tradeDataDetail?.transaction?.bol) && checkRestrictionBol && (
              <TooltipTradeDataLimit sxIcon={{ color: 'white' }} />
            )}
          </TypographyWhite>
          <TranslateText
            buttonProps={{
              color: 'inheritWhite',
            }}
            render={(text) => (
              <TypographyWhite align="left" variant="body2" gutterBottom>
                {renderTextOrDefault(text)}
              </TypographyWhite>
            )}
            text={tradeDataDetail?.transaction?.description}
          />
        </Box>
        <Box display="flex" flexDirection="column" flex={1} minWidth={245} gap={2}>
          {tradeDataDetail?.transaction?.actual_arrival_date && (
            <Stack spacing={1} direction="row" alignItems="center">
              <StyledIconWhite>
                <Calendar />
              </StyledIconWhite>
              <TypographyWhite variant="caption" align="left">
                {dayjs(
                  tradeDataDetail?.transaction?.actual_arrival_date
                ).format(FORMAT_DATE_TIME.FULL_ONLY_DATE)}
              </TypographyWhite>
              <TooltipFormLabel sxIcon={{ color: 'white' }} title={'Arrival Date'} />
            </Stack>
          )}
          <Stack spacing={1} direction="row" alignItems="center">
            <StyledIconWhite>
              <BoxIcon />
            </StyledIconWhite>
            <TypographyWhite variant="caption" align="left">
              {tradeDataDetail?.hs_code}
            </TypographyWhite>
            <TooltipFormLabel sxIcon={{ color: 'white' }} title={'HS Code'} />
          </Stack>
          <Stack spacing={1} direction="row" alignItems="center">
            <FlagCountry
              code={tradeDataDetail?.supplier?.country_code || ''}
              colorDefaultFlag={theme.palette.common.white}
            />
            <TypographyWhite variant="caption" align="left">
              {renderTextOrDefault(tradeDataDetail?.supplier?.country_name)}
            </TypographyWhite>
            <IconFeather
              icon={<ChevronsRight />}
              muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }}
            />
            <FlagCountry
              code={tradeDataDetail?.buyer?.country_code || ''}
              colorDefaultFlag={theme.palette.common.white}
            />
            <TypographyWhite variant="caption" align="left">
              {renderTextOrDefault(tradeDataDetail?.buyer?.country_name)}
            </TypographyWhite>
          </Stack>
          <Stack spacing={0.5} direction="row" alignItems="center">
            <TypographyWhite variant="caption" align="left">
              {renderTextOrDefault(tradeDataDetail?.supplier?.name)}
            </TypographyWhite>
            <IconFeather
              icon={<ChevronsRight />}
              muiProps={{ sx: { color: theme.palette.common.white, fontSize: 16 } }}
            />
            <TypographyWhite variant="caption" align="left">
              {renderTextOrDefault(tradeDataDetail?.buyer?.name)}
            </TypographyWhite>
          </Stack>
        </Box>
      </Stack>
    </StyledBox>
  );
};

export default TradeDataInformation;
